import { useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconSchool } from "@tabler/icons-react";

import { useScreen } from "Screen/ScreenContext";

import useMyMentorings from "./hooks/useMyMentorings";
import MyMentoringsView from "./view";
import MyMentoringDetail from "./single";
import MentoringAttach from "./attach";

/**
 * Creator mentorings controller (§6.11 + §6.10) — mirrors the owner tab's
 * list↔detail switch. Routes (see src/routes.js):
 *   - /mentoring                        → subscribed-mentorings list (§6.11)
 *   - /mentoring/:enrollmentId          → single mentoring detail (§6.11)
 *   - /mentoring/:enrollmentId/attach   → attach a community (§6.10)
 * Detail and attach are their own components so their hooks are never called
 * conditionally.
 */
export default function Mentoring() {
  const { enrollmentId } = useParams();
  const { pathname } = useLocation();

  if (enrollmentId && pathname.endsWith("/attach")) {
    return <MentoringAttach />;
  }

  if (enrollmentId) {
    return <MyMentoringDetail />;
  }

  return <MyMentoringsList />;
}

function MyMentoringsList() {
  const navigate = useNavigate();
  const { t } = useTranslation("member");
  const { setBreadcrumb } = useScreen();
  const { enrollments, loading } = useMyMentorings();

  useEffect(() => {
    document.title = t("myMentorings.documentTitle");
    setBreadcrumb([
      { label: t("myMentorings.breadcrumb"), href: "/mentoring", icon: <IconSchool size={18} /> },
    ]);
    return () => setBreadcrumb(null);
  }, [setBreadcrumb, t]);

  // Awaiting-community card → the attach flow (§6.10).
  function handleAttach(enrollmentId) {
    navigate(`/mentoring/${enrollmentId}/attach`);
  }

  return (
    <MyMentoringsView
      enrollments={enrollments}
      loading={loading}
      onAttach={handleAttach}
    />
  );
}
